/**
 * Welcome Splash Component
 * Displays welcome content when the map first loads
 * Features: mobile responsive, click to dismiss, localStorage persistence, auto-dismiss
 */

import { welcomeContent } from '../config/welcomeContent.js';

export class WelcomeSplash {
  constructor(options = {}) {
    this.options = options;

    this.isVisible = false;
    this.autoDismissTimer = null;
    this.countdownTimer = null;
    this.countdownSeconds = Math.floor(this.options.autoDismissDelay / 1000);
    this.container = null;
    this.content = null;

    this.init();
  }

  async init() {
    try {
      await this.createElements();
      this.setupEventListeners();
      console.log('✅ WelcomeSplash component initialized');
    } catch (error) {
      console.error('❌ Failed to initialize WelcomeSplash:', error);
    }
  }

  async createElements() {
    // Create main container
    this.container = document.createElement('div');
    this.container.className = 'welcome-splash-overlay';
    this.container.id = 'welcomeSplashOverlay';

    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'welcome-splash-backdrop';
    backdrop.addEventListener('click', () => this.dismiss());

    // Create modal content
    const modal = document.createElement('div');
    modal.className = 'welcome-splash-modal';

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = 'welcome-splash-close';
    closeButton.innerHTML = '<i class="fas fa-times"></i>';
    closeButton.setAttribute('aria-label', 'Close welcome message');
    closeButton.addEventListener('click', () => this.dismiss());

    // Create content container
    this.content = document.createElement('div');
    this.content.className = 'welcome-splash-content';

    // Create countdown display
    const countdown = document.createElement('div');
    countdown.className = 'welcome-splash-countdown';
    countdown.id = 'welcomeSplashCountdown';
    countdown.innerHTML = `<span>Auto-close in <strong id="countdownValue">${this.countdownSeconds}</strong> seconds</span>`;

    // Create dismiss button
    const dismissButton = document.createElement('button');
    dismissButton.className = 'welcome-splash-dismiss-btn';
    dismissButton.innerHTML = 'Got it!';
    dismissButton.addEventListener('click', () => this.dismiss());

    // Load embedded content
    this.loadContent();

    // Assemble the modal
    modal.appendChild(closeButton);
    modal.appendChild(this.content);
    modal.appendChild(countdown);
    modal.appendChild(dismissButton);

    this.container.appendChild(backdrop);
    this.container.appendChild(modal);

    // Add to document
    document.body.appendChild(this.container);
  }

  loadContent() {
    // Use embedded content from configuration
    this.content.innerHTML = welcomeContent.html;
  }

  setupEventListeners() {
    if (this.options.enableKeyboardDismiss) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isVisible) {
          this.dismiss();
        }
      });
    }
  }

  show() {
    // Check if user has already dismissed the splash
    if (this.hasBeenDismissed()) {
      return false;
    }

    if (!this.container) {
      console.warn('WelcomeSplash container not initialized');
      return false;
    }

    this.isVisible = true;
    this.container.style.display = 'flex';

    // Trigger animation
    requestAnimationFrame(() => {
      this.container.classList.add('visible');
    });

    // Start auto-dismiss countdown
    this.startCountdown();

    console.log('📢 Welcome splash displayed');
    return true;
  }

  dismiss() {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.container.classList.remove('visible');

    // Clear timers
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    // Hide after animation
    setTimeout(() => {
      this.container.style.display = 'none';
    }, 300);

    // Remember dismissal
    this.markAsDismissed();

    // Notify other components the splash is gone
    document.dispatchEvent(new CustomEvent('welcomeSplashDismissed'));

    console.log('👋 Welcome splash dismissed');
  }

  startCountdown() {
    let remainingSeconds = this.countdownSeconds;
    const countdownElement = document.getElementById('countdownValue');

    this.countdownTimer = setInterval(() => {
      remainingSeconds--;
      if (countdownElement) {
        countdownElement.textContent = remainingSeconds;
      }

      if (remainingSeconds <= 0) {
        this.dismiss();
      }
    }, 1000);

    // Set auto-dismiss timer
    this.autoDismissTimer = setTimeout(() => {
      this.dismiss();
    }, this.options.autoDismissDelay);
  }

  hasBeenDismissed() {
    try {
      return localStorage.getItem(this.options.storageKey) === 'true';
    } catch (error) {
      console.warn('Failed to read localStorage:', error);
      return false;
    }
  }

  markAsDismissed() {
    try {
      localStorage.setItem(this.options.storageKey, 'true');
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
  }

  // Public methods for external control
  forceShow() {
    // Force show even if previously dismissed
    this.show();
  }

  reset() {
    // Reset dismissal state
    try {
      localStorage.removeItem(this.options.storageKey);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  destroy() {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    this.container = null;
    this.content = null;
    console.log('🧹 WelcomeSplash component destroyed');
  }
}